import { useId, useState } from "react";
import PropTypes from "prop-types";
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Bandage,
  Check,
  ChevronDown,
  Minus,
  MoreHorizontal,
  Plus,
  Repeat,
  Trash2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import ItemNotices from "./ItemNotices";
import { itemReason, planLine, setLine, whyLines } from "./lib/format";
import { MAX_SETS, MIN_SETS } from "./lib/planEdit";

const MENU_ITEM = "min-h-11 focus:ring-2 focus:ring-ring focus:ring-inset md:min-h-8";
const QUEUED_WORDS = { saving: "saving…", waiting: "not saved yet", refused: "not saved" };

/**
 * One exercise of the session in progress (walkthroughs 4–6): what the plan
 * asks ("3 × 4–6 @ 2 in reserve · 135 lb") with its reason and, on demand,
 * the reason for each number; the sets logged so far — a queued one says
 * "saving…" until mono has it — each a tap away from being changed; and
 * the ways to log the next one: *Log …* logs it as shown in one tap (the
 * plan's target, or the last set again), the other button opens the
 * logging sheet. *Pain* is on every exercise; ⋯ changes the plan (swap the
 * sets left, set count, order, remove).
 *
 * Presentational: the session view owns the data and every action.
 */
function ExerciseCard({
  cardKey,
  name,
  item = null,
  exercise,
  units,
  sets,
  queued = [],
  quick = null,
  warmup = null,
  notices = [],
  index = -1,
  count = 0,
  logged = 0,
  disabled = false,
  warning = null,
  canLog = true,
  reasonUnits = null,
  onQuickLog,
  onOpenLog,
  onEditSet,
  onFixQueued,
  onDiscardQueued,
  onPain,
  onSwap,
  onChangeSets,
  onMove,
  onRemove,
  onSkipToday,
}) {
  const id = useId();
  const [whyOpen, setWhyOpen] = useState(false);
  const role = item?.role ?? exercise?.role;
  // the title says the name; Why doesn't say the reason again (W8)
  const reason = itemReason(item?.reason, name);
  const why = whyLines(item, reason);
  const planned = item?.sets ?? 0;
  const done = logged;
  const complete = item !== null && done >= planned;
  const left = Math.max(0, planned - done);
  const floor = Math.max(MIN_SETS, done);
  const inPlan = item !== null && index >= 0;
  const removeLabel = done > 0 ? `Drop the ${left === 1 ? "set" : "sets"} left` : "Remove from today";
  const offersQuick = Boolean(quick) && !complete;
  // A joint that hurts today: no one-tap Log on the card — and the sheet's
  // button is named for what it is then, not as the main action.
  const showsQuick = offersQuick && !warning;
  const logAnyway = Boolean(warning) && !complete;
  let logLabel = "Log set";
  if (logAnyway) logLabel = "Log a set anyway…";
  else if (offersQuick) logLabel = "Other numbers…";
  else if (complete) logLabel = "Log an extra set";

  let progress = null;
  if (item) {
    progress =
      done > planned
        ? `${done} sets done (${planned} planned)`
        : `${done} of ${planned} ${planned === 1 ? "set" : "sets"} done`;
  }

  return (
    <li data-card={cardKey} className="rounded-lg border bg-card p-4">
      <div className="flex items-start gap-2">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
          <h2 className="m-0 font-display text-lg font-semibold">{name}</h2>
          {role === "main" && item?.day_type && (
            <>
              {" "}
              <Badge variant="secondary">{item.day_type === "heavy" ? "Heavy day" : "Volume day"}</Badge>
            </>
          )}
          {role === "shoulder_health" && (
            <>
              {" "}
              <Badge variant="secondary">Shoulder health</Badge>
            </>
          )}
          {item?.recovery && (
            <>
              {" "}
              <Badge variant="warning">Recovery</Badge>
            </>
          )}
          {/* the exercise's own break, or its muscle's (W10) */}
          {(item?.eased || item?.easing) && (
            <>
              {" "}
              <Badge variant="warning">Easing back in</Badge>
            </>
          )}
          {!item && (
            <>
              {" "}
              <Badge variant="outline">Not in the plan</Badge>
            </>
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          className="h-11 shrink-0 border-destructive/40 px-3 text-destructive hover:bg-destructive/10 hover:text-destructive md:h-8"
          data-action="pain"
          onClick={onPain}
        >
          <Bandage aria-hidden="true" />
          Pain{" "}
          <span className="sr-only">in {name}</span>
        </Button>
        {inPlan && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-11 shrink-0 md:size-8"
                data-action="menu"
                aria-label={`Change ${name} in the plan`}
                title={`Change ${name} in the plan`}
                disabled={disabled}
              >
                <MoreHorizontal aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem className={MENU_ITEM} disabled={left <= 0} onSelect={onSwap}>
                <Repeat aria-hidden="true" />
                {done > 0 ? "Swap the sets left…" : "Swap…"}
              </DropdownMenuItem>
              <DropdownMenuItem
                className={MENU_ITEM}
                disabled={planned >= MAX_SETS}
                onSelect={() => onChangeSets(1)}
              >
                <Plus aria-hidden="true" />
                One more set
              </DropdownMenuItem>
              <DropdownMenuItem
                className={MENU_ITEM}
                disabled={planned <= floor}
                onSelect={() => onChangeSets(-1)}
              >
                <Minus aria-hidden="true" />
                One set fewer
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className={MENU_ITEM} disabled={index <= 0} onSelect={() => onMove(-1)}>
                <ArrowUp aria-hidden="true" />
                Move up
              </DropdownMenuItem>
              <DropdownMenuItem
                className={MENU_ITEM}
                disabled={index >= count - 1}
                onSelect={() => onMove(1)}
              >
                <ArrowDown aria-hidden="true" />
                Move down
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                variant="destructive"
                className={MENU_ITEM}
                disabled={done > 0 && left <= 0}
                onSelect={onRemove}
              >
                <Trash2 aria-hidden="true" />
                {removeLabel}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {item && (
        <p className="mt-1 mb-0 font-medium tabular-nums">{planLine(item, units, exercise)}</p>
      )}
      {reason && (
        <p className="mt-1 mb-0 text-sm text-muted-foreground">
          {reason}
          {/* the engine's prose can't be converted: say whose units it's in */}
          {reasonUnits && (
            <>
              {" "}
              <span className="whitespace-nowrap">(planned in {reasonUnits})</span>
            </>
          )}
        </p>
      )}
      {notices.length > 0 && <ItemNotices notices={notices} />}
      {why.length > 0 && (
        <>
          <Button
            type="button"
            variant="ghost"
            className="mt-1 -ml-3 h-11 px-3 text-primary md:h-8"
            aria-expanded={whyOpen}
            aria-controls={`${id}-why`}
            onClick={() => setWhyOpen(!whyOpen)}
          >
            <ChevronDown aria-hidden="true" className={cn("transition-transform", whyOpen && "rotate-180")} />
            Why these numbers{" "}
            <span className="sr-only">for {name}</span>
          </Button>
          {whyOpen && (
            <dl id={`${id}-why`} className="m-0 grid gap-x-3 gap-y-1 text-sm sm:grid-cols-[auto_1fr]">
              {why.map(({ key, label, text }) => (
                <div key={key} className="contents">
                  <dt className="font-medium">{label}</dt>
                  <dd className="m-0 mb-1 text-muted-foreground sm:mb-0">{text}</dd>
                </div>
              ))}
            </dl>
          )}
        </>
      )}
      {warmup && (
        <p className="mt-2 mb-0 text-sm">
          <span className="font-medium">Warm-up</span> (not logged):{" "}
          <span className="tabular-nums">{warmup}</span>
        </p>
      )}

      {progress && <p className="mt-3 mb-1 text-sm text-muted-foreground">{progress}</p>}
      {sets.length + queued.length > 0 && (
        <ol aria-label={`Sets of ${name}`} className="m-0 mt-1 flex list-none flex-col gap-0.5 p-0">
          {sets.map((set, i) => {
            const line = setLine(set, units, exercise);
            const by = set.source && set.source !== "ui" ? set.logged_by?.username : null;
            return (
              <li key={set.id}>
                <button
                  type="button"
                  // The name carries what the row shows: who logged it, too.
                  aria-label={`Edit set ${i + 1} of ${name}: ${line}${by ? `, by ${by}` : ""}`}
                  className="flex min-h-11 w-full items-center gap-3 rounded-md px-2 text-left transition-colors hover:bg-muted focus-visible:ring-[3px] focus-visible:ring-ring focus-visible:outline-none md:min-h-9"
                  onClick={() => onEditSet(set)}
                >
                  <span className="w-5 shrink-0 text-sm text-muted-foreground tabular-nums">{i + 1}</span>{" "}
                  <span className="min-w-0 flex-1 tabular-nums">{line}</span>
                  {by && (
                    <>
                      {" "}
                      <Badge variant="outline">by {by}</Badge>
                    </>
                  )}
                </button>
              </li>
            );
          })}
          {queued.map((set, i) => {
            const n = sets.length + i + 1;
            const line = setLine(set, units, exercise);
            return (
              <li key={set.client_id} className="flex flex-col gap-1 px-2 py-1">
                <div className="flex min-h-9 items-center gap-3">
                  <span className="w-5 shrink-0 text-sm text-muted-foreground tabular-nums">{n}</span>{" "}
                  <span className="min-w-0 flex-1 tabular-nums">{line}</span>{" "}
                  <Badge variant={set.status === "refused" ? "destructive" : "outline"}>
                    {QUEUED_WORDS[set.status] ?? "saving…"}
                  </Badge>
                </div>
                {set.status !== "refused" && (
                  // Not changeable until mono has it (an edit could race the
                  // answer). Said on screen while it waits; while it's only
                  // saving (well under a second) it's said to screen readers,
                  // not flashed into the layout under the finger.
                  <p className={cn("m-0 ps-8 text-xs text-muted-foreground", set.status !== "waiting" && "sr-only")}>
                    You can change it once it&apos;s saved.
                  </p>
                )}
                {set.status === "refused" && (
                  <div className="flex flex-wrap items-center gap-2 ps-8">
                    <p role="alert" className="m-0 w-full text-sm text-destructive">
                      mono didn&apos;t take this set: {set.error}
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      className="h-11 md:h-8"
                      aria-label={`Fix set ${n} of ${name}`}
                      onClick={() => onFixQueued(set.client_id)}
                    >
                      Fix it
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      className="h-11 text-destructive hover:bg-destructive/10 hover:text-destructive md:h-8"
                      aria-label={`Discard set ${n} of ${name}`}
                      onClick={() => onDiscardQueued(set.client_id)}
                    >
                      Discard it
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}

      {warning && (
        <div className="mt-3 flex flex-col gap-2 rounded-md border border-destructive/40 p-3">
          <p className="m-0 flex items-start gap-2 text-sm">
            <AlertTriangle aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-destructive" />
            <span>{warning}</span>
          </p>
          {inPlan && left > 0 && (
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                className="h-11 md:h-8"
                data-action="warning-swap"
                disabled={disabled}
                onClick={onSwap}
              >
                <Repeat aria-hidden="true" />
                Swap…{" "}
                <span className="sr-only">{name}</span>
              </Button>
              <Button
                type="button"
                variant="outline"
                className="h-11 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive md:h-8"
                data-action="skip-today"
                disabled={disabled}
                onClick={onSkipToday}
              >
                Skip it today<span className="sr-only">: {name}</span>
              </Button>
            </div>
          )}
        </div>
      )}

      {canLog && (
        <div className="mt-3 flex flex-wrap gap-2">
          {showsQuick && (
            <Button
              type="button"
              // A long set ("bodyweight, medium band × 8 @ 2 in reserve") wraps
              // instead of pushing a 390 px phone sideways.
              className="h-auto min-h-11 w-full py-2 text-left whitespace-normal md:min-h-9 md:w-auto"
              data-action="quick"
              onClick={(event) => {
                // A double-click is two clicks: the second lands on this same
                // button, now offering the next set — it must not log it.
                if (event.detail > 1) return;
                onQuickLog(quick);
              }}
            >
              <Check aria-hidden="true" />
              Log {setLine(quick, units, exercise)}{" "}
              <span className="sr-only">for {name}</span>
            </Button>
          )}
          <Button
            type="button"
            variant={offersQuick || complete || logAnyway ? "outline" : "default"}
            className={cn("h-11 md:h-9", !showsQuick && "w-full md:w-auto")}
            data-action="log"
            onClick={onOpenLog}
          >
            {logLabel}{" "}
            <span className="sr-only">{offersQuick || logAnyway ? `for ${name}` : `of ${name}`}</span>
          </Button>
        </div>
      )}
    </li>
  );
}

ExerciseCard.propTypes = {
  /** The exercise key (marks the card for focus moves). */
  cardKey: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  /** Its plan item (TrainingPlanItem); null for sets logged off the plan. */
  item: PropTypes.object,
  /** The catalog's exercise, when the catalog has it. */
  exercise: PropTypes.object,
  units: PropTypes.string.isRequired,
  /** Logged sets (TrainingSet), oldest first. */
  sets: PropTypes.arrayOf(PropTypes.object).isRequired,
  /** Queued sets: TrainingSetWrite + `status` (saving | waiting | refused) and `error`. */
  queued: PropTypes.arrayOf(PropTypes.object),
  /** The set one tap logs (a TrainingSetWrite body), or null. */
  quick: PropTypes.object,
  /** The warm-up line, for the first main lift before its first set. */
  warmup: PropTypes.string,
  /** What the engine said about doing it today, when the person brought it in (W3). */
  notices: PropTypes.arrayOf(PropTypes.string),
  /** Its place in the plan (−1 off the plan) and the plan's length. */
  index: PropTypes.number,
  count: PropTypes.number,
  /** Sets of it logged, queued ones included. */
  logged: PropTypes.number,
  /** Plan changes off (one is saving). */
  disabled: PropTypes.bool,
  /** It loads a joint that hurts today: the words (no one-tap Log). */
  warning: PropTypes.string,
  /** False: no way to log (the session closed itself). */
  canLog: PropTypes.bool,
  /** The plan's units when they aren't the card's: its reason is in those. */
  reasonUnits: PropTypes.string,
  onQuickLog: PropTypes.func.isRequired,
  onOpenLog: PropTypes.func.isRequired,
  onEditSet: PropTypes.func.isRequired,
  onFixQueued: PropTypes.func.isRequired,
  onDiscardQueued: PropTypes.func.isRequired,
  onPain: PropTypes.func.isRequired,
  onSwap: PropTypes.func,
  onChangeSets: PropTypes.func,
  onMove: PropTypes.func,
  onRemove: PropTypes.func,
  /** The warning's "Skip it today": drop the sets left. */
  onSkipToday: PropTypes.func,
};

export default ExerciseCard;
